import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { investmentInfo, siteConfig } from "../data/mock";
import { ArrowRight, Check } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

export default function InvestmentPage() {
  const [heroRef, heroInView] = useInView();
  const [detailsRef, detailsInView] = useInView();

  return (
    <main>
      <section
        ref={heroRef}
        className="pt-32 md:pt-44 pb-20 md:pb-28 px-6 md:px-12 bg-[#FAFAF7]"
      >
        <div className="max-w-[1400px] mx-auto">
          <span
            className={`font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block transition-all duration-1000 ${
              heroInView ? "opacity-100" : "opacity-0"
            }`}
          >
            Investment
          </span>
          <h1
            className={`font-display text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[1.05] mb-8 max-w-3xl whitespace-pre-line transition-all duration-1000 delay-200 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {investmentInfo.headline}
          </h1>
          <p
            className={`font-body text-stone-500 text-lg leading-relaxed max-w-2xl transition-all duration-1000 delay-400 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {investmentInfo.intro}
          </p>
        </div>
      </section>

      {/* Budget Highlight */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-stone-950">
        <div className="max-w-[1400px] mx-auto text-center">
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-8 block">
            Minimum Investment
          </span>
          <h2 className="font-display text-6xl md:text-7xl lg:text-8xl text-white mb-6">
            {siteConfig.minBudget}
          </h2>
          <p className="font-body text-white/40 text-base max-w-xl mx-auto leading-relaxed">
            Our custom homes start at {siteConfig.minBudget}. This ensures every project
            receives the design attention, material quality, and craftsmanship that
            defines the Catapult experience.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section ref={detailsRef} className="py-28 md:py-40 px-6 md:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {investmentInfo.sections.map((section, i) => (
              <div
                key={section.title}
                className={`transition-all duration-1000 ${
                  detailsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${i * 200}ms` }}
              >
                <h3 className="font-display text-2xl md:text-3xl text-stone-900 mb-8">
                  {section.title}
                </h3>
                <div className="space-y-5">
                  {section.items.map((item, j) => (
                    <div key={j} className="flex items-start gap-4">
                      <div className="w-5 h-5 mt-0.5 flex-shrink-0 flex items-center justify-center bg-[#B8977E]/10">
                        <Check size={12} className="text-[#B8977E]" />
                      </div>
                      <span className="font-body text-stone-600 text-base leading-relaxed">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`mt-20 pt-16 border-t border-stone-200 transition-all duration-1000 delay-400 ${
              detailsInView ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <h3 className="font-display text-2xl md:text-3xl text-stone-900 mb-2">
                  Is Catapult right for you?
                </h3>
                <p className="font-body text-stone-500 text-base">
                  Let's have a conversation to find out. No commitment, no pressure.
                </p>
              </div>
              <Link
                to="/catapult/journey"
                className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase px-8 py-4 bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300 group"
              >
                Start Your Journey
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
